<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import Photoswipe from 'photoswipe'
import 'photoswipe/style.css'

import type { Props } from './types'
const props = withDefaults(defineProps<Props>(), {
  imageMultiplier: 5
})

const lightbox = ref()

onMounted(() => {
  initLightbox()
})

const initLightbox = () => {
  lightbox.value = new PhotoSwipeLightbox({
    gallery: `#${props.id}`,
    mainClass: 'app-images-grid',
    childSelector: 'a',
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 1.4,
    maxZoomLevel: 3,
    pswpModule: Photoswipe,
    wheelToZoom: true,
    preload: [1, 2]
  })

  lightbox.value.init()

  lightbox.value.addFilter('useContentPlaceholder', () => false)
}

onUnmounted(() => {
  destroyLightbox()
})

const destroyLightbox = () => {
  if (lightbox.value) {
    lightbox.value.destroy()
    lightbox.value = null
  }
}

</script>

<script lang="ts">
export default {
  name: 'AppImagesGrid'
}
</script>

<template>
  <div
    :id
    class="app-images-grid"
    :class="`grid-cols-${Math.min(images.length, 2)}`">
    <a
      v-for="(img, index) in images"
      :key="index"
      :href="`${img}?auto=compress,enhance,format&w=1000&h=1000`"
      :data-lh-id="`${id}-images-grid-gallery-img-${index}`"
      :data-pswp-width="800"
      :data-pswp-height="800"
      :class="{ 'hidden': index >= maxImagesInGrid}"
      target="_blank">
      <img
        v-if="index < maxImagesInGrid"
        alt="images-grid"
        :data-lh-id="`${id}-images-grid-gallery-img-${index}`"
        class="images-grid__img rounded-8 w-full min-w-116 h-116 object-cover"
        :src="`${img}?auto=compress,enhance,format&w=300&h=300`">
    </a>
  </div>
</template>

<style lang="scss">
.app-images-grid {
  @apply grid gap-8;

  .pswp {
    &__img {
      object-fit: contain !important;
    }

    &__button {
      &--arrow--next,
      &--arrow--prev {
        @apply w-50 h-50 mx-16;
        @apply bg-white rounded-full shadow-[0_0_10px_#0000004f];
        @apply grid place-content-center;
        @apply hover:bg-white;
      }

      &--arrow--next {
        & .pswp__icn {
          @apply fill-primary-600 top-10 -left-4 scale-[.6] rotate-180;
        }
      }

      &--arrow--prev {
        & .pswp__icn {
          @apply fill-primary-600 top-10 -left-0 scale-[.6];
        }
      }
    }

    &__icn-shadow {
      @apply hidden;
    }

    &__counter {
      @apply text-18 font-medium;
    }
  }
}

</style>
